import { defineRule } from "vee-validate";

import {
  required,
  email,
  min,
  max,
  confirmed,
  alpha_spaces
} from "@vee-validate/rules";
import { defineNuxtPlugin } from "nuxt/app";
import { rutNormalizer, rutValidator } from "@ctrpdev/cl-rut-validator";

export default defineNuxtPlugin(() => {
  defineRule("required", (value: string) => {
    if (required(value)) {
      return true;
    }
    return "Este campo es obligatorio.";
  });
  defineRule("email", (value: string) => {
    if (!value) {
      return "El campo correo electrónico es obligatorio.";
    }
    if (email(value)) {
      return true;
    }
    return "El correo electrónico no es válido.";
  });
  defineRule("min", (value: string, [limit]: [number], ctx) => {
    if (min(value, [limit])) {
      return true;
    }
    const field = ctx.field;
    const fieldTranslations = {
      password: "contraseña",
      passwordRepeat: "contraseña",
      email: "correo electrónico",
      names: "nombres",
      lastnames: "apellidos",
      gender: "sexo",
      birthdate: "fecha de nacimiento",
      educationLevel: "nivel educacional"
    };
    const fieldName =
      fieldTranslations[field as keyof typeof fieldTranslations] || field;

    return `El campo ${fieldName} debe tener al menos ${ctx.rule?.params || "un"} caracteres.`;
  });
  defineRule("max", max);

  defineRule("date_format", (value: string) => {
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (value && dateRegex.test(value)) {
      return true;
    }
    return "El formato de la fecha debe ser válido.";
  });
  defineRule("valid_date_range", (value: string) => {
    const today = new Date();
    const inputDate = new Date(value.split("/").reverse().join("-"));
    const minDate = new Date(
      today.getFullYear() - 100,
      today.getMonth(),
      today.getDate()
    );

    if (isNaN(inputDate.getTime())) {
      return "La fecha ingresada no es válida.";
    }

    if (inputDate > today) {
      return "La fecha no puede ser mayor a la fecha actual.";
    }

    if (inputDate < minDate) {
      return "La fecha no puede tener más de 100 años de antigüedad.";
    }

    return true;
  });
  defineRule("two_last_name", (value: string | null) => {
    if (!value || value.trim() === "") {
      return false;
    }
    const words = value.trim().split(/\s+/);

    if (words.length !== 2) {
      return "Este campo debe contener ambos apellidos.";
    }

    const isValidWord = (word: string) =>
      /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ]+$/.test(word);
    if (!isValidWord(words[0] || "") || !isValidWord(words[1] || "")) {
      return "Ambos apellidos deben contener solo letras.";
    }

    return true;
  });
  defineRule("same_password", (value: string, [target]: [string]) => {
    if (confirmed(value, [target])) {
      return true;
    }
    return "Las contraseñas no coinciden.";
  });
  defineRule("same_email", (value: string, [target]: [string]) => {
    if (confirmed(value, [target])) {
      return true;
    }
    return "Los correos electrónicos no coinciden.";
  });
  defineRule("alpha_spaces", (value: string) => {
    if (alpha_spaces(value, {})) {
      return true;
    }
    return "Este campo solo puede contener letras y espacios.";
  });
  defineRule("rut", (value: string) => {
    if (!value) {
      return "El RUT es obligatorio.";
    }
    const rut = rutNormalizer(value);
    if (rutValidator(rut)) {
      return true;
    }
    return "El RUT ingresado no es válido.";
  });
  defineRule("document_number_format", (value: string) => {
    if (/^[a-zA-Z0-9.-]{8,30}$/.test(value)) {
      return true;
    }
    return "Debe tener un mínimo 8 caracteres de letras y números.";
  });
  defineRule("required_autocomplete_input", (value: string) => {
    if (required(value)) {
      return true;
    }
    return "Debe seleccionar una opción de la lista.";
  });
  defineRule("required_search_occupation", (value: string) => {
    if (required(value)) {
      return true;
    }
    return "Selecciona una opción o ingresa tu cargo/carrera si no aparece en la lista";
  });
  defineRule(
    "different",
    (value: string, [target, message]: [string, string?]) => {
      if (value !== target) {
        return true;
      }
      return message ?? "Este valor debe ser diferente al anterior.";
    }
  );
  defineRule("min_age", (value: string, [years]: [number]) => {
    if (!value) return true;
    const [day, month, year] = value.split("/").map(Number);
    const birthdate = new Date(year!, month! - 1, day);
    if (isNaN(birthdate.getTime())) return true;
    const today = new Date();
    const minBirthdate = new Date(
      today.getFullYear() - years,
      today.getMonth(),
      today.getDate()
    );
    if (birthdate <= minBirthdate) {
      return true;
    }
    return `Debes tener al menos ${years} años para registrarte.`;
  });
});
